import { useMemo, useEffect, useCallback } from 'react';
import {
    useEventGroup,
    useEventItem,
    useEventsLoadDone,
    useMarketsLoadDone,
    useEventsChunkRequest,
    useMarketsChunkRequest,
    useEventGroupsLoadDone,
    useMarketIdsFromMarketInfos,
} from 'modules/common/hooks';
import MojitoPresentation from 'mojito/presentation';
import MojitoServices from 'mojito/services';
import { isEmpty, pick } from 'mojito/utils';
import MojitoCore from 'mojito/core';
import PrebuiltBets from 'modules/prebuilt-bets/index.js';
import { useAggregatedMarketInfos } from 'modules/event-markets/hooks';
import PrebuiltBetBuilderCouponSkeleton from './skeleton/index.js';
import { shallowEqual, useSelector } from 'react-redux';

const {
    Text,
    FlexPane,
    Button,
    EventTimingStatus,
    LoaderSuspense,
    ParticipantIconsInline,
    scoreboardUtils: { sortParticipants },
} = MojitoPresentation.Components;
const { getSeparatorTranslationKey } = MojitoPresentation.Utils.ParticipantSeparator;
const { EventFormatter } = MojitoPresentation.Utils;
const { IntentActionTrigger } = MojitoPresentation.Base.Intent;
const { NavigationPathBuilder } = MojitoPresentation.Base.Navigation;
const AnalyticsContextBuilder = MojitoCore.Presentation.AnalyticsContext.ContextBuilder;
const AnalyticsContextExtender = MojitoCore.Presentation.AnalyticsContext.ContextExtender;
const { selectEvents } = MojitoServices.SportsContent.Events.selectors;
const { types: MarketGroupTypes } = MojitoServices.SportsContent.MarketGroups;
const { utils: EventsUtils } = MojitoServices.SportsContent.Events;
const VideoStreamsUtils = MojitoServices.VideoStreaming.utils;
const { GROUP_TYPES } = MarketGroupTypes;
const EMPTY_OBJECT = {};
const EMPTY_ARRAY = [];

const PrebuiltBetBuilderCoupon = props => {
    const {
        pbbbGroupId,
        selectedGroupId,
        mojitoTools: { config, appContext, instanceId, stringResolver },
        onDataLoad,
    } = props;
    const pbbbGroups = useEventGroup(pbbbGroupId);
    const groupId = selectedGroupId || pbbbGroups?.[0]?.id;
    const selectedGroup = groupId && pbbbGroups?.find(group => group.id === groupId);
    const preloadDone = usePBBBContentPreload(selectedGroup, instanceId);
    const pbbbGroupLoadDone = useEventGroupsLoadDone([pbbbGroupId]);
    const noContent = pbbbGroupLoadDone && !selectedGroup;
    const loadDone = noContent || (pbbbGroupLoadDone && preloadDone);

    const analyticsContext = useMemo(
        () =>
            new AnalyticsContextBuilder()
                .withAnalyticsPath(
                    `PrebuiltBetBuilderCoupon${groupId ? ' on a sports page pivot' : ''}`
                )
                .build(),
        [groupId]
    );

    useEffect(() => {
        loadDone && onDataLoad(pbbbGroupId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadDone]);

    const skeleton = (
        <PrebuiltBetBuilderCouponSkeleton config={config.prebuiltBetBuilderCouponSkeleton} />
    );

    if (noContent) {
        return null;
    }

    return (
        <>
            <LoaderSuspense
                config={config.skeletonLoader}
                isContentPending={!loadDone}
                loader={skeleton}
            >
                <AnalyticsContextExtender value={analyticsContext}>
                    <FlexPane config={config.container}>
                        {selectedGroup?.events.map(event => (
                            <PBBBEvent
                                key={event.id}
                                eventId={event.id}
                                config={config}
                                routeResolver={appContext.routeResolver}
                                shouldRequestData={preloadDone}
                                stringResolver={stringResolver}
                            />
                        ))}
                    </FlexPane>
                </AnalyticsContextExtender>
            </LoaderSuspense>
        </>
    );
};

const PBBBEvent = ({ eventId, config, routeResolver, shouldRequestData, stringResolver }) => {
    const eventItem = useEventItem(eventId, shouldRequestData);
    const eventLoaded = useEventsLoadDone([eventId]);
    const noContent = eventLoaded && !eventItem;
    const isRacing = EventsUtils.isRaceEventType(eventItem?.eventType);

    const onEventClick = useCallback(() => {
        const { sportId, details, eventType } = eventItem;
        EventsUtils.isRaceEventType(eventType)
            ? IntentActionTrigger.showMeetingsRaces(sportId, details.meetingId, eventId)
            : IntentActionTrigger.showSportEvent(sportId, eventId);
    }, [eventItem, eventId]);

    const prebuiltMarketGroupIds = useMemo(
        () =>
            eventItem?.marketGroups
                ?.filter(group => group.type === GROUP_TYPES.PREBUILT_MATCH_ACCA)
                .map(group => group.id),
        [eventItem]
    );

    const prebuiltMarketInfos = useAggregatedMarketInfos(prebuiltMarketGroupIds, shouldRequestData);

    const marketIds = useMarketIdsFromMarketInfos(
        Object.values(prebuiltMarketInfos)?.filter(Boolean)
    );

    const contentLoadDone = eventLoaded && !isEmpty(prebuiltMarketInfos);

    const betItems = useMemo(() => {
        return marketIds.map(id => ({
            eventId: eventId,
            marketId: id,
        }));
    }, [marketIds, eventId]);

    const getHrefLink = () => {
        return NavigationPathBuilder.sportEventPath(eventItem?.sportId, eventId, routeResolver);
    };

    const participants = useMemo(
        () => sortParticipants(eventItem?.participants, eventItem?.homeAway),
        [eventItem?.participants, eventItem?.homeAway]
    );

    if (noContent) {
        return null;
    }

    return (
        <>
            {config.showEventHeader && (
                <FlexPane config={config.headerContainer}>
                    <Button
                        class={'ta-EventHeaderButton'}
                        hrefLink={getHrefLink()}
                        onClick={onEventClick}
                        config={config.eventButton}
                    >
                        {isRacing ? (
                            <Text config={config.eventNameLabel}>
                                {EventFormatter.formatEventName(eventItem.name)}
                            </Text>
                        ) : (
                            <ParticipantIconsInline
                                participants={participants}
                                sportId={eventItem?.sportId}
                                separator={stringResolver.resolveString(
                                    getSeparatorTranslationKey(eventItem?.homeAway)
                                )}
                                config={config.participantsIconInline}
                            />
                        )}
                        {eventItem && (
                            <EventTimingStatus
                                eventItem={eventItem}
                                config={config.eventTimingStatus}
                                hasStreaming={VideoStreamsUtils.isStreamAvailable(eventItem)}
                                hasClockDetails={EventsUtils.hasInPlayClockDetails(eventItem)}
                                inplayTime={EventsUtils.getEventInplayTime(eventItem)}
                            />
                        )}
                    </Button>
                </FlexPane>
            )}
            <FlexPane config={config.prebuiltBetsContainer}>
                <PrebuiltBets
                    config={config.prebuiltBets}
                    betItems={betItems || EMPTY_ARRAY}
                    isContentLoaded={contentLoadDone}
                />
            </FlexPane>
        </>
    );
};

const usePBBBContentPreload = (eventsGroup, instanceId) => {
    const eventIds = useMemo(() => eventsGroup?.events.map(event => event.id), [eventsGroup]);
    const eventsLoadDone = useEventsLoadDone(eventIds);

    const eventItems = useSelector(
        state => (eventsLoadDone ? pick(selectEvents(state), ...eventIds) : EMPTY_OBJECT),
        shallowEqual
    );
    const marketGroupIds = useMemo(
        () =>
            Object.values(eventItems)
                .flatMap(event =>
                    event.marketGroups?.filter(
                        group => group.type === GROUP_TYPES.PREBUILT_MATCH_ACCA
                    )
                )
                .map(group => group.id),
        [eventItems]
    );
    const prebuiltMarketInfos = useAggregatedMarketInfos(marketGroupIds);
    const marketIds = useMarketIdsFromMarketInfos(
        Object.values(prebuiltMarketInfos)?.filter(Boolean)
    );

    // Preload all events and markets in one chunk
    useEventsChunkRequest(eventIds, instanceId);
    useMarketsChunkRequest(marketIds, instanceId);

    const marketsLoadDone = useMarketsLoadDone(marketIds);

    return eventsLoadDone && marketsLoadDone;
};

export default PrebuiltBetBuilderCoupon;
